import { type PropsWithChildren, useEffect } from 'react';

import { useXYChartData } from '../hooks/useXYChartData.js';
import { useSetState } from '../store/selectors.js';
import { getCurrentDomainMap } from '../utils/get-current-domain-axis.js';
import { isZoomOutAllowed } from '../utils/is-zoom-out-allowed.js';

/**
 * Syncs domain-dependent state (currentDomain, zoomOutEnabled) into the store
 * after the data provider has computed axes and scale domains.
 *
 * This component exists because the store is placed above the data provider
 * in the provider tree (to allow legend filtering), but domain/zoom state
 * depends on processed data.
 */
export const XYChartStoreDomainSync = ({ children }: PropsWithChildren) => {
  const { axes, axisIdToScaleDomain } = useXYChartData();
  const setState = useSetState();

  useEffect(() => {
    const currentDomainMap = getCurrentDomainMap(axes);
    const zoomOutAllowed = isZoomOutAllowed(axes, axisIdToScaleDomain);

    setState((prev) => ({
      ...prev,
      zoom: {
        ...prev.zoom,
        zoomOutEnabled: zoomOutAllowed,
      },
      currentDomain: currentDomainMap,
    }));
  }, [axes, axisIdToScaleDomain, setState]);

  return children;
};
